import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoBox, NavFooter } from '../';
import { ModelVariant } from '../../api';
import {
  ErrorMessage,
  Input,
  InputClearButton,
  InputContainer,
  InputStickyContainer,
  Question,
} from '../../App.styles';
import { StepperContext } from '../../contexts';
import { ScreenOutcomeType, useHandleOutcome, useMetrics } from '../../hooks';
import { ReasonOutcomeType } from '../../hooks/useHandleOutcome';
import { AppOutcome, StepKey } from '../../types';
import {
  getModelOutcome,
  getSearchResult,
  iconUrl,
  isAmazonHost,
  metricEvents,
  MetricProperty,
  TestId,
} from '../../utils';
import Button from '../Button';
import RadioGroup, { RadioOption } from '../RadioGroup/RadioGroup';

interface BackplateSelectionProps {
  onBack: () => void;
}

const BackplateSelection: React.FC<BackplateSelectionProps> = ({ onBack }) => {
  const { t } = useTranslation('common');
  const { track } = useMetrics();
  const { handleOutcome } = useHandleOutcome();
  const { stepperData, updateStepperData } = useContext(StepperContext);
  const { brand, model } = stepperData;
  const [filter, setFilter] = useState('');
  const [variantList, setVariantList] = useState<RadioOption<ModelVariant>[]>(
    []
  );
  const [variant, setVariant] = useState<ModelVariant>();
  const [allVariants, setAllVariants] = useState<RadioOption<ModelVariant>[]>(
    []
  );

  useEffect(() => {
    if (model?.variants) {
      setAllVariants(
        model?.variants
          .filter((variant) => variant.backplate_number)
          .map((variant, index) => ({
            id: index.toString(),
            label: variant.backplate_number,
            value: variant,
          }))
      );
    }
  }, [model]);

  useEffect(() => {
    if (variant) {
      setFilter(variant.backplate_number);
    }
  }, [setFilter]);

  useEffect(() => {
    const filteredVariants = Array.from(
      new Map(
        getSearchResult(allVariants, 'label', filter).map((v) => [v.label, v])
      ).values()
    );

    setVariantList(filteredVariants);
  }, [filter, allVariants]);

  const handleModelSelectionOutcome = (
    outcome: AppOutcome,
    reason: ReasonOutcomeType,
    searchText?: string
  ) => {
    handleOutcome({
      value: outcome,
      screen: ScreenOutcomeType.SelectBackplate,
      reason,
      brand,
      model,
      variant,
      searchText,
    });
  };

  const handleNeedHelp = () => {
    track(metricEvents.needHelpBtn, {
      [MetricProperty.pageName]: StepKey.BackplateSelection,
    });
    handleModelSelectionOutcome(
      AppOutcome.InfoNeeded,
      ReasonOutcomeType.HelpClick
    );
  };

  return (
    <div
      className="row h-100 flex-column"
      data-testid={TestId.BackplateSelection}
    >
      <Question className="col-12">{t('prompts.backplateSelection')}</Question>

      <div className="col-12 mt-3">
        <InfoBox
          title={t('help.backplate.title')}
          description={t('help.backplate.description')}
        ></InfoBox>
      </div>

      <InputStickyContainer className="col-12 py-3" sticky background="white">
        <InputContainer>
          {filter.length > 0 && (
            <InputClearButton
              aria-label="close"
              onClick={() => {
                setFilter('');
                setVariant(undefined);
              }}
            >
              <img src={iconUrl.close} alt={t('textFields.closeBtn')} />
            </InputClearButton>
          )}
          <Input
            type="text"
            hasError={!variantList.length}
            placeholder={t('placeholders.modelFilter')}
            aria-label={t('placeholders.modelFilter')}
            className="form-control"
            value={filter}
            onChange={(e) => {
              setVariant(undefined);
              setFilter(e.target.value);
            }}
          />
        </InputContainer>
        {!variantList.length && (
          <ErrorMessage className="mt-3">
            {t('help.noResults.errorMessage')}
          </ErrorMessage>
        )}
      </InputStickyContainer>

      <div className="flex-grow-1">
        {variantList.length ? (
          <RadioGroup
            groupName="variants"
            options={variantList}
            value={variant}
            setValue={(val) => setVariant(val)}
          ></RadioGroup>
        ) : (
          <span
            role="button"
            className="d-block mt-3"
            onClick={() => {
              const searchText = filter.toLowerCase().trim();
              handleModelSelectionOutcome(
                AppOutcome.InfoNeeded,
                ReasonOutcomeType.NoSearchResultsClick,
                searchText
              );
            }}
          >
            <InfoBox
              isHelp
              title={t('help.noResults.title')}
              description={t('help.noResults.description')}
            ></InfoBox>
          </span>
        )}
      </div>

      <NavFooter>
        <Button
          type="button"
          disabled={!variant}
          onClick={() => {
            if (variant) {
              updateStepperData({
                variant,
              });
              handleModelSelectionOutcome(
                getModelOutcome(variant.support),
                ReasonOutcomeType.Complete
              );
            }
          }}
          data-testid={TestId.ButtonNext}
        >
          {t('buttons.continue')}
        </Button>
        {isAmazonHost() && (
          <>
            <Button type="button" ghost onClick={onBack} data-testid={TestId.ButtonBack}>
              {t('header.back')}
            </Button>
            <div style={{ marginRight: 'auto' }} />
          </>
        )}
        <Button type="button" ghost onClick={handleNeedHelp} data-testid={TestId.ButtonNeedHelp}>
          {t('buttons.needHelp')}
        </Button>
      </NavFooter>
    </div>
  );
};

export default BackplateSelection;
